// components/MultiPlatformPixelScript/metaPixel.ts — Meta (fbq) pixel firing
import type { FireContext } from './types';
import { buildMetaUserDataRaw } from '../../utils/pixelPii';
import { makeEmbedSkipper } from '../../utils/embedTracking';
import { ensureFbq } from './metaSdk';

export function fireMetaPixels(ctx: FireContext): void {
    const { configs, logPrefix, contentName, contentIds, orderData, value, userData, eventId, fireWithRetry, embedded, embedMode } = ctx;
    const skip = makeEmbedSkipper(embedded, embedMode, logPrefix);
    const metaUserDataRaw = buildMetaUserDataRaw(userData || undefined);

    const metaConfigs = configs.filter(c => c.platform === 'meta');
    if (metaConfigs.length > 0) {
        const isRealFbq = ensureFbq(logPrefix);

        if (!(window as any)._our_fbq_inited) {
            (window as any)._our_fbq_inited = new Set<string>();
        }

        // WYSIWYG: event dropdown langsung di-fire (pilih InitiateCheckout → kirim itu).
        const metaEventName = metaConfigs[0].eventName;

        fireWithRetry(
            'Meta',
            metaEventName,
            isRealFbq,
            () => {
                const fbq = (window as any).fbq;
                const ourInited: Set<string> = (window as any)._our_fbq_inited || new Set();
                (window as any)._our_fbq_inited = ourInited;

                metaConfigs.forEach(config => {
                    config.pixelIds.filter(id => id?.trim()).forEach(pixelId => {
                        if (!ourInited.has(pixelId)) {
                            ourInited.add(pixelId);
                            // Advanced Matching via argumen init ke-3 (terdokumentasi Meta).
                            // JANGAN fbq('set','userData',...) — itu jalur khusus
                            // pixel Shopify (butuh opt-in ShopifyAppIntegratedPixel),
                            // di pixel biasa selalu ditolak + warning (2026-10-11).
                            fbq('init', pixelId, metaUserDataRaw || undefined);
                            console.log(`[${logPrefix} Meta] init: ${pixelId}`, metaUserDataRaw ? Object.keys(metaUserDataRaw) : '(tanpa userData)');
                        } else {
                            console.log(`[${logPrefix} Meta] skip init (already done by us): ${pixelId}`);
                        }
                    });
                });

                // Embed: PageView milik LP induk — iframe jangan double-count.
                if (!skip('Meta', 'PageView')) {
                    fbq('track', 'PageView');
                    console.log(`[${logPrefix} Meta] PageView tracked`);
                }

                metaConfigs.forEach(config => {
                    // Embed smart: event top-funnel milik LP induk — konversi tetap jalan.
                    if (skip('Meta', config.eventName)) return;
                    const params: any = {
                        content_name: contentName || 'Form',
                        currency: orderData?.currency || 'IDR',
                        content_type: 'product'
                    };

                    if (orderData) {
                        params.value = orderData.value;
                        params.order_id = orderData.orderId;
                        if (orderData.contentIds) params.content_ids = orderData.contentIds;
                        if (orderData.quantity) params.num_items = orderData.quantity;
                    } else if (value !== undefined) {
                        params.value = value;
                    }
                    // content_ids form = slug form — WAJIB sama dengan content_ids di LP
                    // (Berdu) supaya funnel LP→form tersambung di Meta.
                    if (!params.content_ids && contentIds && contentIds.length > 0) {
                        params.content_ids = contentIds;
                    }

                    // eventID = argumen KE-4 (bukan di dalam params — diabaikan Meta).
                    // SAMA dgn CAPI event_id (orderId / eventId eksplisit) → dedup browser↔CAPI.
                    const eventIdOpt = orderData?.orderId ? { eventID: orderData.orderId } : eventId ? { eventID: eventId } : undefined;
                    if (eventIdOpt) fbq('track', metaEventName, params, eventIdOpt);
                    else fbq('track', metaEventName, params);
                    console.log(`[${logPrefix} Meta] ${metaEventName} FIRED!`, params);
                });
            }
        );
    }
}
